import React, { useState } from "react";
import { ActionIcon, Box, Code, CopyButton, Group, SimpleGrid, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconCheck, IconCopy, IconSearch } from "@tabler/icons-react";
import { FeeBumpData, Wallet, matchesSearch } from "../helpers/feeBumpWallets";

// hover/focus for the wizard's choice buttons, and its 150 ms step fade (nothing else moves)
export const WIZARD_CSS = `
.rz-choice { display: block; width: 100%; text-align: left; color: #fff; font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 8px; background: rgba(255, 255, 255, 0.03);
  transition: border-color 120ms, background-color 120ms; line-height: 1.35; overflow-wrap: anywhere; }
/* only with a real pointer: a touch screen keeps :hover at the last tap, on the next step's button */
@media (hover: hover) { .rz-choice:hover { border-color: rgba(251, 146, 60, 0.7); background: rgba(251, 146, 60, 0.08); } }
.rz-choice:focus-visible { outline: 2px solid #fb923c; outline-offset: 2px; }
.rz-choice[data-selected] { border-color: #fb923c; background: rgba(251, 146, 60, 0.12); }
@keyframes rz-wizard-in { from { opacity: 0; } to { opacity: 1; } }
.rz-wizard-step { animation: rz-wizard-in 150ms ease-out; }
@media (prefers-reduced-motion: reduce) { .rz-wizard-step, .rz-choice { animation: none; transition: none; } }
`;

export const WizardStyles: React.FC = () => <style>{WIZARD_CSS}</style>;

/** A big answer button: its visible text is its label. */
export const Choice: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  compact?: boolean;
  selected?: boolean;
}> = ({ onClick, children, compact, selected }) => (
  <UnstyledButton
    className="rz-choice"
    onClick={onClick}
    data-selected={selected || undefined}
    style={{ padding: compact ? "8px 10px" : "14px 16px", fontSize: compact ? 13 : 16, minHeight: compact ? 40 : 52 }}
  >
    {children}
  </UnstyledButton>
);

const Quoted: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/("[^"]+")/).map((part, i) =>
      part.startsWith('"') && part.endsWith('"') && part.length > 2 ? (
        <Text key={i} span c="white" fw={700} inherit>
          {part}
        </Text>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    )}
  </>
);

/** Step text with the wallet's own "labels" picked out, and `commands` as copyable code. */
export const Labelled: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/(`[^`]+`)/).map((part, i) =>
      part.startsWith("`") && part.endsWith("`") && part.length > 2 ? (
        <Group key={i} gap={6} wrap="nowrap" align="flex-start" my={6} component="span" style={{ display: "flex" }}>
          <Code block style={{ flex: 1, minWidth: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere", backgroundColor: "rgba(0, 0, 0, 0.4)" }}>
            {part.slice(1, -1)}
          </Code>
          <CopyButton value={part.slice(1, -1)}>
            {({ copied, copy }) => (
              <ActionIcon variant="outline" color={copied ? "teal" : "gray"} onClick={copy} aria-label="Copy command">
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            )}
          </CopyButton>
        </Group>
      ) : (
        <Quoted key={i} text={part} />
      )
    )}
  </>
);

/** The wallet grid: a search box, self-custody wallets, the apps that hold bitcoin for you, "Something else". */
const WalletPicker: React.FC<{
  data: FeeBumpData;
  onPick: (wallet: Wallet) => void;
  selectedId?: string | null;
}> = ({ data, onPick, selectedId }) => {
  const [query, setQuery] = useState("");
  const shown = data.wallets.filter((wallet) => matchesSearch(wallet, query));
  const own = shown.filter((wallet) => wallet.kind !== "custodial");
  const custodial = shown.filter((wallet) => wallet.kind === "custodial");

  const grid = (wallets: Wallet[]) => (
    <SimpleGrid cols={{ base: 3, sm: 4 }} spacing={8} verticalSpacing={8}>
      {wallets.map((wallet) => (
        <Choice key={wallet.id} compact selected={wallet.id === selectedId} onClick={() => onPick(wallet)}>
          {wallet.name}
        </Choice>
      ))}
    </SimpleGrid>
  );

  return (
    <Stack gap="sm">
      {/* also used before there's a wizard (Where do I find the transaction ID?) */}
      <WizardStyles />
      <TextInput
        aria-label="Search wallets"
        placeholder="Search wallets"
        leftSection={<IconSearch size={16} />}
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        styles={{ input: { backgroundColor: "rgba(0, 0, 0, 0.3)", border: "1px solid rgba(255, 255, 255, 0.15)", color: "white" } }}
      />
      {own.length > 0 && grid(own)}
      {custodial.length > 0 && (
        <Box>
          <Text size="xs" c="dimmed" mb={6}>
            Exchanges and apps that hold your bitcoin for you
          </Text>
          {grid(custodial)}
        </Box>
      )}
      {shown.length === 0 && (
        <Text size="sm" c="dimmed">
          No wallet by that name here. Choose "Something else".
        </Text>
      )}
      <SimpleGrid cols={{ base: 3, sm: 4 }} spacing={8}>
        <Choice compact selected={selectedId === data.other.id} onClick={() => onPick(data.other)}>
          {data.other.name}
        </Choice>
      </SimpleGrid>
    </Stack>
  );
};

export default WalletPicker;
